import styled from "styled-components";
import { palette, fonts, radii } from "../../styles/palette";

export const MicrophonePermissionModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: ${palette.overlayDark};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 12px;
    align-items: flex-start;
    padding-top: 20px;
  }
`;

export const MicrophonePermissionModalCard = styled.div`
  background: ${palette.white};
  border: 1px solid ${palette.border};
  border-radius: ${radii.md};
  padding: 32px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 18px 48px rgba(35, 35, 35, 0.3);
  position: relative;
  margin: auto;

  @media (max-width: 768px) {
    padding: 24px 20px;
    max-height: calc(100vh - 40px);
    border-radius: ${radii.md};
  }

  @media (max-width: 480px) {
    padding: 20px 16px;
    max-height: calc(100vh - 24px);
  }
`;

export const MicrophonePermissionModalHeader = styled.div`
  position: relative;
  padding-top: 16px;
  padding-bottom: 8px;
  margin-bottom: 24px;
  text-align: center;

  @media (max-width: 768px) {
    padding-top: 12px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    padding-top: 8px;
    margin-bottom: 16px;
  }
`;

export const MicrophonePermissionModalCloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  background: transparent;
  border: none;
  color: ${palette.textSecondary};
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${palette.surfaceMuted};
    color: ${palette.red};
  }
`;

export const MicrophonePermissionIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  color: ${palette.red};

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
`;

export const MicrophonePermissionModalTitle = styled.h2`
  font-family: ${fonts.heading};
  color: ${palette.navy};
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 12px 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 10px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 8px;
  }
`;

export const MicrophonePermissionModalSubtitle = styled.p`
  color: ${palette.textSecondary};
  font-size: 16px;
  margin: 0;
  text-align: center;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

export const MicrophonePermissionInstructions = styled.div`
  background: ${palette.surfaceMuted};
  border: 1px solid ${palette.border};
  border-radius: ${radii.md};
  padding: 20px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    margin-bottom: 16px;
  }
`;

export const MicrophonePermissionInstructionItem = styled.div`
  color: ${palette.textSecondary};
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 12px;
  padding-left: 24px;
  position: relative;

  &:before {
    content: "•";
    position: absolute;
    left: 8px;
    color: ${palette.red};
    font-weight: bold;
    font-size: 18px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 13px;
    padding-left: 20px;
    margin-bottom: 10px;
    line-height: 1.5;

    &:before {
      left: 6px;
      font-size: 16px;
    }
  }

  @media (max-width: 480px) {
    font-size: 12px;
    padding-left: 18px;
    margin-bottom: 8px;
    line-height: 1.4;

    &:before {
      left: 4px;
      font-size: 14px;
    }
  }
`;

export const ErrorMessage = styled.div`
  background: ${palette.red}12;
  border: 1px solid ${palette.red}40;
  border-radius: ${radii.sm};
  padding: 16px;
  margin-bottom: 20px;
  color: ${palette.red};
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  line-height: 1.5;

  @media (max-width: 768px) {
    padding: 12px;
    margin-bottom: 16px;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    margin-bottom: 12px;
    font-size: 12px;
  }
`;

export const MicrophonePermissionModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 8px;
    flex-direction: column;
  }
`;

export const MicrophonePermissionModalButton = styled.button<{
  variant: "primary" | "secondary";
}>`
  background: ${({ variant }) =>
    variant === "primary" ? palette.red : palette.white};
  color: ${({ variant }) =>
    variant === "primary" ? palette.white : palette.navy};
  border: ${({ variant }) =>
    variant === "primary" ? "none" : `1.5px solid ${palette.navy}`};
  border-radius: ${radii.pill};
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: ${({ variant }) =>
    variant === "primary" ? `0 4px 14px ${palette.redGlow}` : "none"};
  min-width: 140px;

  &:hover {
    background: ${({ variant }) =>
      variant === "primary" ? palette.redHover : palette.navy};
    color: ${palette.white};
    transform: translateY(-1px);
    box-shadow: ${({ variant }) =>
      variant === "primary"
        ? `0 6px 18px ${palette.redGlow}`
        : `0 6px 18px ${palette.shadowMedium}`};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 15px;
    min-width: 120px;
  }

  @media (max-width: 480px) {
    padding: 12px 20px;
    font-size: 14px;
    width: 100%;
    min-width: unset;
  }
`;
